import React from 'react';

/**
 * Input Component - Touch-friendly text input with consistent styling
 * 
 * @param {Object} props
 * @param {string} props.label - Input label text
 * @param {string} props.error - Error message to display
 * @param {string} props.type - Input type (text, email, password, etc.)
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.required - Mark as required
 * @param {*} props...rest - Additional input props
 */
const Input = React.forwardRef(({
    label,
    error,
    type = 'text',
    placeholder,
    className = '',
    required = false,
    ...props
}, ref) => {
    const inputClassName = `
    w-full px-4 py-3 text-base
    border-2 rounded-lg
    min-h-touch
    transition-colors duration-200
    focus:outline-none focus:ring-2 focus:ring-forest-500 focus:border-transparent
    disabled:bg-gray-100 dark:disabled:bg-navy-800 disabled:cursor-not-allowed
    bg-white dark:bg-navy-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500
    ${error ? 'border-red-500' : 'border-gray-300 hover:border-gray-400 dark:border-gray-700 dark:hover:border-gray-500'}
    ${className}
  `;

    return (
        <div className="w-full">
            {label && (
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    {label}
                    {required && <span className="text-red-500 ml-1">*</span>}
                </label>
            )}
            <input
                ref={ref}
                type={type}
                placeholder={placeholder}
                className={inputClassName}
                required={required}
                {...props}
            />
            {error && (
                <p className="mt-2 text-sm text-red-600">{error}</p>
            )}
        </div>
    );
});

Input.displayName = 'Input';

export default Input;
